import type { ViewFieldsFragment } from '@salescore/client-api'
import type { ViewQueryList } from '@salescore/core'
import { App, Input } from 'antd'
import { Array, pipe } from 'effect'
import { t } from 'i18next'
import type { ReactElement } from 'react'
import { useRecoilValue } from 'recoil'

import { hasCustomSearchQuery } from '../../../domain/viewConfig/searchQuery'
import { useSearchQuery } from '../../../hooks/useSearchQuery'
import { useLoadingState } from '../../../recoil/records/hooks'
import { useRefetchMutation } from '../../../recoil/records/mutations/useRefetchMutation'
import { useSearchableFields } from '../../../recoil/records/selectors/useSearchableFields'
import { changesAtom } from '../../../recoil/recordsHistory'
import { buildSearchableFieldsFormModalProperties } from './SearchableFieldsFormModal'

export const SearchQueryInput = ({
  view,
  query,
  setHasChangeToViewSchema,
}: {
  view: ViewFieldsFragment
  query: ViewQueryList
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const { message, modal } = App.useApp()
  const searchableFields = useSearchableFields(query)
  const recordsMutation = useRefetchMutation(view, query)
  const [searchQuery, setSearchQuery] = useSearchQuery(() => {
    void recordsMutation.refetch({ kind: 'reset' })
  })
  const loading = useLoadingState()
  // 検索条件 SQL が設定されているとフリーワード検索はその SQL だけで成立し、検索対象項目は参照されない
  // (useGetQueryWithFilterTree が extra.searchQuery を優先して読む)。活性判定もプレースホルダも
  // この 1 つの軸で分ける。項目数で分けると、項目を選んだうえで SQL も設定したシートで
  // 「検索対象にならない項目名」を並べて案内することになる。
  const hasCustomQuery = hasCustomSearchQuery(query.extra?.searchQuery)
  const disabled = searchableFields.length === 0 && !hasCustomQuery
  const searchableFieldsPlaceholder =
    searchableFields.length === 0
      ? t(`検索設定がされていません`)
      : `${t(`{{keyword}}で検索`, {
          keyword: pipe(
            searchableFields,
            Array.map((x) => x.meta.label.replace(`(${t(`必須`)})`, '')),
            Array.dedupe,
            Array.join(','),
          ),
        })}
                ...`
  const changes = useRecoilValue(changesAtom)

  return (
    <div
      className={disabled ? `cursor-pointer` : ``}
      // disabledするとSearchのonClickも効かなくなるので、divをラップしている
      onClick={() => {
        if (disabled) {
          const m = modal.confirm(
            buildSearchableFieldsFormModalProperties({
              onAfterFinish: () => {
                m.destroy()
              },
              query,
              setHasChangeToViewSchema,
            }),
          )
        }
      }}
      data-e2e="search-query-input"
    >
      <Input.Search
        defaultValue={searchQuery}
        disabled={disabled}
        // onChange={(e) => {
        // eslint-disable-next-line sonarjs/todo-tag
        //   // TODO: lazy set
        //   // setSearchQuery(e.target.value)
        // }}
        // enterButton={<span>
        //   <SearchOutlined/> 検索
        // </span>}
        placeholder={hasCustomQuery ? t(`キーワードで検索`) : searchableFieldsPlaceholder}
        loading={loading.isTrue}
        onSearch={(query) => {
          setSearchQuery(query)
        }}
        allowClear
        style={{
          width: 300,
          cursor: 'pointer',
        }}
        onMouseDown={() => {
          if (changes.length > 0) {
            message.warning(t('検索すると変更情報が失われます。'))
          }
        }}
        className="px-3"
      />
    </div>
  )
}
